import { getToken, clearToken, clearUser } from './storage';
import type { Task } from '@jizhu/shared/types';

const API_BASE = import.meta.env.VITE_API_URL || 'https://jizhu.huanyan.org';

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  if (!token) throw new Error('Not logged in');
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(API_BASE + path, { ...init, headers });
  if (res.status === 401) {
    clearToken(); clearUser();
    window.location.href = '/m/login';
    throw new Error('Session expired');
  }
  return res;
}

export interface AuthResult {
  token: string;
  user: { id: string; email: string; display_name: string };
}

export async function login(email: string, password: string): Promise<AuthResult> {
  const res = await fetch(API_BASE + '/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Login failed');
  return (await res.json()) as AuthResult;
}

export async function register(email: string, password: string, displayName: string): Promise<AuthResult> {
  const res = await fetch(API_BASE + '/api/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, display_name: displayName }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Register failed');
  return (await res.json()) as AuthResult;
}

export async function listTasks(opts: { from?: number; to?: number; status?: string } = {}): Promise<Task[]> {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', String(opts.from));
  if (opts.to) params.set('to', String(opts.to));
  if (opts.status) params.set('status', opts.status);
  const res = await authedFetch('/api/tasks?' + params.toString());
  if (!res.ok) throw new Error('List failed');
  return (await res.json()).tasks;
}

export async function snoozeTask(taskId: string, toTime: number, preset: string): Promise<Task> {
  const res = await authedFetch(`/api/tasks/${taskId}/snooze`, {
    method: 'POST', body: JSON.stringify({ to_time: toTime, preset }),
  });
  if (!res.ok) throw new Error('Snooze failed');
  return (await res.json()).task as Task;
}

export async function markDone(taskId: string): Promise<Task> {
  const res = await authedFetch(`/api/tasks/${taskId}/done`, { method: 'POST' });
  if (!res.ok) throw new Error('Done failed');
  return (await res.json()).task as Task;
}

export async function deleteTask(taskId: string): Promise<void> {
  await authedFetch(`/api/tasks/${taskId}`, { method: 'DELETE' });
}
